import { FormLabel, Grid, Stack } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

import {
  AutoComplete,
  FormContainer,
  FormInput,
  Treturn,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import FormTextEditor from "../../../../../../components/UI/TextEditor/FormTextEditor";
import MainApi from "../../../../../../services/mainApi.service";
import {
  createTasySchedMenu,
  getTasySchedMenuById,
  updateTasySchedMenuById,
} from "../../../../../../services/tasySchedMenu.service";
import { tasySchedMenuSchema, TTasySchedMenuForm } from "./TasySchedMenuSchema";

const translationPath = "page.settings.schedule.tasySchedMenu.";

const TICKET_AUTOMATION_RECEIVED_RULE_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomationReceivedRule/";

const ticketAutomationReceivedRuleService = new MainApi(
  TICKET_AUTOMATION_RECEIVED_RULE_ROUTE,
);

const TasySchedMenuFields = () => {
  const { t } = useTranslation();

  const { control, setValue } = useFormContext<TTasySchedMenuForm>();

  const ticketAutomationReceivedRule = useWatch({
    control,
    name: "ticketAutomationReceivedRule",
  });

  const hasTicketAutomationReceivedRule = !!ticketAutomationReceivedRule?.id;

  useEffect(() => {
    if (hasTicketAutomationReceivedRule) {
      setValue("customMessage", "", { shouldDirty: true, shouldValidate: true });
    }
  }, [hasTicketAutomationReceivedRule, setValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <AutoComplete
          getOptionLabel={(option) => option.name}
          name="ticketAutomationReceivedRule"
          label={t(translationPath + "ticket_automation_received_rule")}
          size="small"
          request={ticketAutomationReceivedRuleService.list}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormInput
          size="small"
          name="name"
          fullWidth
          label={t(translationPath + "name") || "Name"}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <FormInput
          size="small"
          name="displayOrder"
          fullWidth
          type="number"
          label={t(translationPath + "display_order") || "Display order"}
        />
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={1}>
          <FormLabel>{t(translationPath + "custom_message")}</FormLabel>
          <FormTextEditor
            name="customMessage"
            readOnly={hasTicketAutomationReceivedRule}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

const TasySchedMenu = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } =
    useFetch(createTasySchedMenu);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTasySchedMenuById,
  );

  const { sendRequest: get, loading: gLoading } =
    useFetch(getTasySchedMenuById);

  const getPayload = (v: TTasySchedMenuForm) => ({
    id: v.id,
    ticketAutomationReceivedRuleId: v.ticketAutomationReceivedRule?.id,
    name: v.name,
    customMessage: v.ticketAutomationReceivedRule?.id ? null : v.customMessage,
    displayOrder: Number(v.displayOrder),
  });

  const saveHandler = (v: TTasySchedMenuForm) => save(getPayload(v));

  const updateHandler = (v: TTasySchedMenuForm, id: string) =>
    update({
      item: {
        ...getPayload(v),
        id,
      },
      id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TTasySchedMenuForm>> => {
      const { data, success } = await get(id);

      if (data) {
        return {
          data: {
            ...data,
            ticketAutomationReceivedRule:
              data.ticketAutomationReceivedRule ?? null,
            displayOrder: data.displayOrder.toString(),
          },
          success: true,
        };
      }

      return { data, success };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TTasySchedMenuForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "menu_scheduling_portal_app")}
      schema={tasySchedMenuSchema}
      subtitle={t(translationPath + "new_menu_scheduling_portal_app")}
      subtitleWatchField={"name"}
      fields={() => <TasySchedMenuFields />}
    />
  );
};

export default TasySchedMenu;
